import React, { useState, useMemo } from 'react';
import { ContentGapAnalysis } from '../types/sitemap';
import { CreateTodoRequest } from '../types/todo';

interface ContentGapsPanelProps {
  contentGaps: ContentGapAnalysis | null;
  isVisible: boolean;
  onAddTodo?: (todo: Omit<CreateTodoRequest, 'userEmail' | 'propertyUrl'>) => Promise<any>;
}

const ContentGapsPanel: React.FC<ContentGapsPanelProps> = ({
  contentGaps,
  isVisible,
  onAddTodo
}) => {
  const [showOrphanedContent, setShowOrphanedContent] = useState(true);
  const [showContentOpportunities, setShowContentOpportunities] = useState(true);
  const [orphanedFilter, setOrphanedFilter] = useState('');
  const [opportunityFilter, setOpportunityFilter] = useState('');
  const [addingTodos, setAddingTodos] = useState<Set<string>>(new Set());

  const filteredOrphanedContent = useMemo(() => {
    if (!contentGaps?.orphanedContent) return [];
    return contentGaps.orphanedContent.filter(item =>
      (item.loc && item.loc.toLowerCase().includes(orphanedFilter.toLowerCase())) ||
      (item.title && item.title.toLowerCase().includes(orphanedFilter.toLowerCase()))
    );
  }, [contentGaps?.orphanedContent, orphanedFilter]);

  const filteredOpportunities = useMemo(() => {
    if (!contentGaps?.contentOpportunities) return [];
    return contentGaps.contentOpportunities.filter(item =>
      item.query && item.query.toLowerCase().includes(opportunityFilter.toLowerCase())
    );
  }, [contentGaps?.contentOpportunities, opportunityFilter]);

  const handleAddTodo = async (type: 'orphaned' | 'opportunity', data: any) => {
    if (!onAddTodo) return;
    
    const todoKey = type === 'orphaned' ? data.loc : data.query;
    setAddingTodos(prev => new Set(prev).add(todoKey));

    try {
      await onAddTodo({
        title: type === 'orphaned' 
          ? `Review orphaned content: ${data.loc.split('/').pop() || 'Page'}`
          : `Create content for: "${data.query}"`,
        description: type === 'orphaned'
          ? `This page exists in your sitemap but gets no search traffic. Consider optimizing or updating: ${data.loc}`
          : `High-value search query (${data.clicks} clicks, ${data.impressions} impressions) has no dedicated landing page. Consider creating content to target this query.`,
        priority: type === 'orphaned' ? 'medium' : 'high',
        sourcePanel: 'content-gaps'
      });
    } catch (error) {
      console.error('Failed to add todo:', error);
    } finally {
      setAddingTodos(prev => {
        const newSet = new Set(prev);
        newSet.delete(todoKey);
        return newSet;
      });
    }
  };


  if (!isVisible || !contentGaps) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center">
          📊 Content Gaps Analysis
        </h2>
        <div className="text-sm text-gray-600">
          {contentGaps.summary.coveragePercentage}% sitemap coverage
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 p-3 rounded-lg">
          <div className="text-sm font-medium text-blue-900">Total Sitemap URLs</div>
          <div className="text-xl font-bold text-blue-600">{contentGaps.totalSitemapUrls}</div>
        </div>
        <div className="bg-green-50 p-3 rounded-lg">
          <div className="text-sm font-medium text-green-900">Search Coverage</div>
          <div className="text-xl font-bold text-green-600">{contentGaps.matchedUrls}</div>
        </div>
        <div className="bg-orange-50 p-3 rounded-lg">
          <div className="text-sm font-medium text-orange-900">Orphaned Content</div>
          <div className="text-xl font-bold text-orange-600">{contentGaps.summary.orphanedCount}</div>
        </div>
        <div className="bg-purple-50 p-3 rounded-lg">
          <div className="text-sm font-medium text-purple-900">Content Opportunities</div>
          <div className="text-xl font-bold text-purple-600">{contentGaps.summary.opportunityCount}</div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Orphaned Content Section */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => setShowOrphanedContent(!showOrphanedContent)}
              className="flex items-center text-lg font-medium text-gray-900 hover:text-gray-700"
            >
              <span className={`transform transition-transform ${showOrphanedContent ? 'rotate-90' : ''}`}>
                ▶
              </span>
              <span className="ml-2">🔍 Orphaned Content ({contentGaps.summary.orphanedCount})</span>
            </button>
          </div>

          {showOrphanedContent && (
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Filter orphaned content..."
                value={orphanedFilter}
                onChange={(e) => setOrphanedFilter(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500"
              />
              
              <div className="max-h-96 overflow-y-auto">
                {filteredOrphanedContent.length > 0 ? (
                  <div className="space-y-2">
                    {filteredOrphanedContent.map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded border">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {item.loc.split('/').pop() || 'Page'}
                          </div>
                          <div className="text-xs text-gray-600 truncate">
                            <a 
                              href={item.loc} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              {item.loc}
                            </a>
                          </div>
                          {item.lastmod && (
                            <div className="text-xs text-gray-500">
                              Last modified: {new Date(item.lastmod).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                        {onAddTodo && (
                          <button
                            onClick={() => handleAddTodo('orphaned', item)}
                            disabled={addingTodos.has(item.loc)}
                            className="ml-3 px-3 py-1 text-xs bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded hover:from-orange-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                          >
                            {addingTodos.has(item.loc) ? '...' : '+ Todo'}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 text-center py-4">
                    {orphanedFilter ? 'No orphaned content matches your filter' : 'No orphaned content found'}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Content Opportunities Section */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => setShowContentOpportunities(!showContentOpportunities)}
              className="flex items-center text-lg font-medium text-gray-900 hover:text-gray-700"
            >
              <span className={`transform transition-transform ${showContentOpportunities ? 'rotate-90' : ''}`}>
                ▶
              </span>
              <span className="ml-2">🎯 Content Opportunities ({contentGaps.summary.opportunityCount})</span>
            </button>
          </div>

          {showContentOpportunities && (
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Filter content opportunities..."
                value={opportunityFilter}
                onChange={(e) => setOpportunityFilter(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500"
              />
              
              <div className="max-h-96 overflow-y-auto">
                {filteredOpportunities.length > 0 ? (
                  <div className="space-y-2">
                    {filteredOpportunities.map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded border">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900">
                            "{item.query}"
                          </div>
                          <div className="flex items-center space-x-4 text-xs text-gray-600">
                            <span>👆 {item.clicks} clicks</span>
                            <span>👁 {item.impressions} impressions</span>
                            <span>📍 Pos. {item.position.toFixed(1)}</span>
                            <span>📈 {(item.ctr * 100).toFixed(1)}% CTR</span>
                          </div>
                        </div>
                        {onAddTodo && (
                          <button
                            onClick={() => handleAddTodo('opportunity', item)}
                            disabled={addingTodos.has(item.query)}
                            className="ml-3 px-3 py-1 text-xs bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded hover:from-orange-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                          >
                            {addingTodos.has(item.query) ? '...' : '+ Todo'}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 text-center py-4">
                    {opportunityFilter ? 'No opportunities match your filter' : 'No content opportunities found'}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentGapsPanel;